import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Stepper, 
  Step, 
  StepLabel, 
  Button, 
  Paper,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import MultiPaymentForm from './MultiPaymentForm';
import { getMarinaSettings } from '../firebase/services/marinaSettingsService';
import { createReservation } from '../firebase/services/reservationService';
import { Reservation } from '../models/types';
import { useAuth } from '../contexts/AuthContext';
import { createCustomer, searchCustomers } from '../firebase/services/customerVesselService';

interface ReservationPaymentProps {
  reservationData: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt' | 'paymentStatus' | 'createdBy' | 'version'>;
  onReservationComplete: (reservationId: string) => void;
  onCancel: () => void;
}

const ReservationPayment: React.FC<ReservationPaymentProps> = ({
  reservationData,
  onReservationComplete,
  onCancel
}) => {
  const { currentUser } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [depositAmount, setDepositAmount] = useState(0);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [marinaSettings, setMarinaSettings] = useState<any>(null);

  // Calculate deposit amount on component mount
  useEffect(() => {
    const calculateDeposit = async () => {
      try {
        const settings = await getMarinaSettings();
        console.log('Marina Settings in ReservationPayment:', settings);
        console.log('Marina Settings Reservation Rules:', settings.reservationRules);
        setMarinaSettings(settings);
        
        // Check if reservation rules exist
        if (!settings.reservationRules) {
          console.error('Reservation rules not found in marina settings');
          setPaymentError('Failed to calculate deposit amount. Please contact support.');
          setDepositAmount(0);
          return;
        }
        
        const { depositRequired, depositAmount, depositType } = settings.reservationRules;
        
        console.log('Deposit Required:', depositRequired);
        console.log('Deposit Amount:', depositAmount);
        console.log('Deposit Type:', depositType);
        console.log('Total Price:', reservationData.finalPrice);
        
        if (!depositRequired) {
          console.log('No deposit required, using full amount:', reservationData.finalPrice);
          setDepositAmount(0);
          return;
        }
        
        // Calculate deposit amount if percentage-based
        let calculatedDepositAmount;
        if (depositType === 'percentage') {
          calculatedDepositAmount = (reservationData.finalPrice * depositAmount) / 100;
          console.log(`Calculating ${depositAmount}% deposit: $${calculatedDepositAmount.toFixed(2)}`);
        } else {
          // Use fixed deposit amount
          calculatedDepositAmount = depositAmount;
          console.log(`Using fixed deposit amount: $${depositAmount.toFixed(2)}`);
        }
        
        // Round the deposit amount to nearest quarter if needed
        const roundedDepositAmount = Math.round(calculatedDepositAmount * 100) / 100;
        setDepositAmount(roundedDepositAmount);
        
        // Store the reservation data in localStorage for the MultiPaymentForm to access
        localStorage.setItem('reservationData', JSON.stringify({
          ...reservationData,
          deposit: roundedDepositAmount,
          depositType: depositType,
          originalDepositAmount: depositAmount // Store the original setting value
        }));
        
        // Also update marinaSettings in localStorage to ensure consistent values
        localStorage.setItem('marinaSettings', JSON.stringify(settings));
      } catch (error) {
        console.error('Error calculating deposit:', error);
        setPaymentError('Failed to calculate deposit amount. Please try again.');
      }
    };
    
    calculateDeposit();
  }, [reservationData.finalPrice]);

  const handlePaymentComplete = async (paymentId: string, status: string, processor: string) => {
    try {
      setProcessing(true);
      
      console.log('Creating reservation with payment info:');
      console.log('- Payment ID:', paymentId);
      console.log('- Status:', status);
      console.log('- Deposit Amount:', depositAmount);
      console.log('- Total Price:', reservationData.finalPrice);
      
      // Create or find customer
      let customerId: string;
      try {
        // Try to find existing customer by email
        const existingCustomers = await searchCustomers(reservationData.customerDetails.email);
        const existingCustomer = existingCustomers.find(
          c => c.email.toLowerCase() === reservationData.customerDetails.email.toLowerCase()
        );
        
        if (existingCustomer) {
          customerId = existingCustomer.id;
          console.log('Found existing customer:', customerId);
        } else {
          // Create new customer
          const newCustomer = await createCustomer({
            firstName: reservationData.customerDetails.firstName,
            lastName: reservationData.customerDetails.lastName,
            email: reservationData.customerDetails.email.toLowerCase(),
            phone: reservationData.customerDetails.phone,
            vessels: []
          });
          customerId = newCustomer.id;
          console.log('Created new customer:', customerId);
        }
      } catch (err) {
        console.error('Error handling customer:', err);
        // Fall back to original customerId if customer creation fails
        customerId = reservationData.customerId;
      }
      
      // Enhance payment information object with comprehensive tracking
      const paymentInfo = {
        method: 'credit_card' as const,                      // Payment method
        id: paymentId,                                       // Square transaction ID
        amount: depositAmount > 0 ? depositAmount : reservationData.finalPrice, // Actual amount paid
        totalAmount: reservationData.finalPrice,             // Total reservation amount
        depositAmount: depositAmount > 0 ? depositAmount : 0, // Deposit amount if applicable
        remainingAmount: depositAmount > 0 ? (reservationData.finalPrice - depositAmount) : 0, // Amount still due
        depositPaid: depositAmount > 0,                      // Whether this is a deposit payment
        processor: processor as 'square',                    // Payment processor used
        status: (status === 'COMPLETED' || status === 'succeeded' ? 'paid' : 'pending') as 'paid' | 'pending' | 'failed', // Payment status with correct type
        paymentDate: new Date().toISOString(),               // Date/time of payment
        refunded: false,                                     // Track if payment has been refunded
        refundAmount: 0,                                     // Amount refunded (if applicable)
        refundDate: null,                                    // Date of refund (if applicable)
        refundId: null,                                      // Refund transaction ID (if applicable)
        paymentDetails: {                                    // Additional payment details
          cardBrand: null,                                   // Will be updated if available from processor
          last4: null,                                       // Last 4 digits of card (if available)
          paymentMethod: 'card'                              // Default to card
        }
      };

      // Payment history entry (for the paymentHistory array)
      const paymentHistoryEntry = {
        id: paymentId,
        amount: depositAmount > 0 ? depositAmount : reservationData.finalPrice,
        method: 'credit_card',
        status: status === 'COMPLETED' || status === 'succeeded' ? 'paid' : 'pending',
        date: new Date().toISOString(),
        refunded: false
      };
      
      // Create the reservation with enhanced payment information
      let id;
      try {
        const reservationWithCreatedBy: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'> = {
          ...reservationData,
          customerId: customerId, // Use the actual customer ID
          createdBy: currentUser?.uid || 'guest',
          paymentStatus: status === 'COMPLETED' || status === 'succeeded' ? 'paid' : 'pending',
          payment: paymentInfo,
          paymentHistory: [paymentHistoryEntry],
          version: 1 // Initialize version for optimistic locking
        };

        id = await createReservation(reservationWithCreatedBy);
        
        console.log('Reservation created successfully with ID:', id);
      } catch (createError) {
        console.error('Error creating reservation:', createError);
        
        // If there's a Firebase auth issue for the public booking flow, try with manual creation
        const { createManualReservation } = await import('../firebase/services/publicReservationService');
        
        if (createManualReservation) {
          console.log('Attempting manual reservation creation as public user');
          id = await createManualReservation({
            ...reservationData,
            customerId: customerId, // Use the actual customer ID
            paymentStatus: status === 'COMPLETED' || status === 'succeeded' ? 'paid' : 'pending',
            payment: paymentInfo,
            paymentHistory: [paymentHistoryEntry],
            version: 1 // Initialize version for optimistic locking
          });
          
          console.log('Manual reservation created successfully with ID:', id);
        } else {
          throw createError;
        }
      }
      
      setReservationId(id);
      setActiveStep(2); // Move to confirmation step
    } catch (error) {
      console.error('Error creating reservation:', error);
      setPaymentError('Failed to create reservation. Please contact support.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentError = (error: string) => {
    setPaymentError(error);
    // Ensure we stay on the payment page (step 1) when a payment error occurs
    setActiveStep(1);
  };

  const handleConfirmReservation = () => {
    if (reservationId) {
      onReservationComplete(reservationId);
    }
  };

  const steps = ['Review Reservation', 'Payment', 'Confirmation'];

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Complete Your Reservation
      </Typography>
      
      <Stepper activeStep={activeStep} sx={{ my: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {paymentError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {paymentError}
        </Alert>
      )}
      
      {activeStep === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Reservation Details
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              <strong>Vessel:</strong> {reservationData.vesselName}
            </Typography>
            <Typography variant="body1">
              <strong>Date:</strong> {new Date(reservationData.date).toLocaleDateString()}
            </Typography>
            <Typography variant="body1">
              <strong>Time:</strong> {reservationData.startTime} - {reservationData.endTime}
            </Typography>
            <Typography variant="body1">
              <strong>Party Size:</strong> {reservationData.partySize}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ mb: 2 }}>
            {reservationData.originalPrice && reservationData.discountAmount && (
              <>
                <Typography variant="body1">
                  <strong>Original Price:</strong> ${reservationData.originalPrice.toFixed(2)}
                </Typography>
                <Typography variant="body1" color="error">
                  <strong>Discount:</strong> -${reservationData.discountAmount.toFixed(2)}
                  {reservationData.promoCode && ` (Promo: ${reservationData.promoCode})`}
                </Typography>
              </>
            )}
            <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1 }}>
              <strong>Total Price:</strong> ${reservationData.finalPrice.toFixed(2)}
            </Typography>
            {depositAmount > 0 && (
              <Typography variant="body1">
                <strong>Deposit Required:</strong> ${depositAmount.toFixed(2)}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => setActiveStep(1)}
            >
              Proceed to Payment
            </Button>
          </Box>
        </Box>
      )}
      
      {activeStep === 1 && (
        <Box>
          <Paper elevation={3} sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 2 }}>
            {depositAmount > 0 ? (
              <>
                <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                  <strong>Paying Deposit Amount: ${depositAmount.toFixed(2)}</strong>
                  <Typography variant="caption" display="block" color="text.secondary">
                    The remaining balance of ${(reservationData.finalPrice - depositAmount).toFixed(2)} will be due at check-in
                  </Typography>
                </Typography>
                <MultiPaymentForm
                  amount={depositAmount}
                  customerInfo={reservationData.customerDetails}
                  onPaymentComplete={handlePaymentComplete}
                  onPaymentError={handlePaymentError}
                />
              </>
            ) : (
              <MultiPaymentForm
                amount={reservationData.finalPrice}
                customerInfo={reservationData.customerDetails}
                onPaymentComplete={handlePaymentComplete}
                onPaymentError={handlePaymentError}
              />
            )}
          </Paper>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button variant="outlined" onClick={() => setActiveStep(0)}>
              Back
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              onClick={onCancel}
            >
              Cancel Reservation
            </Button>
          </Box>
        </Box>
      )}
      
      {activeStep === 2 && (
        <Box>
          <Alert severity="success" sx={{ mb: 3 }}>
            Your reservation has been confirmed!
          </Alert>
          
          <Typography variant="h6" gutterBottom>
            Reservation Confirmation
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              <strong>Reservation ID:</strong> {reservationId}
            </Typography>
            <Typography variant="body1">
              <strong>Vessel:</strong> {reservationData.vesselName}
            </Typography>
            <Typography variant="body1">
              <strong>Date:</strong> {new Date(reservationData.date).toLocaleDateString()}
            </Typography>
            <Typography variant="body1">
              <strong>Time:</strong> {reservationData.startTime} - {reservationData.endTime}
            </Typography>
            <Typography variant="body1">
              <strong>Party Size:</strong> {reservationData.partySize}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ mb: 2 }}>
            {reservationData.originalPrice && reservationData.discountAmount && (
              <>
                <Typography variant="body1">
                  <strong>Original Price:</strong> ${reservationData.originalPrice.toFixed(2)}
                </Typography>
                <Typography variant="body1" color="error">
                  <strong>Discount Applied:</strong> -${reservationData.discountAmount.toFixed(2)}
                  {reservationData.promoCode && ` (Promo: ${reservationData.promoCode})`}
                </Typography>
              </>
            )}
            <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1 }}>
              <strong>Total Price:</strong> ${reservationData.finalPrice.toFixed(2)}
            </Typography>
            {depositAmount > 0 && (
              <Typography variant="body1">
                <strong>Deposit Paid:</strong> ${depositAmount.toFixed(2)}
              </Typography>
            )}
          </Box>
          
          <Typography variant="body2" sx={{ mt: 2, mb: 3 }}>
            A confirmation email has been sent to {reservationData.customerDetails.email}.
            Please arrive 15 minutes before your scheduled time.
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleConfirmReservation}
            >
              Complete
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ReservationPayment; 